import { type FC, type JSX, useState } from "react"
import { Navigate, useParams } from "react-router"
import styled from "styled-components"

import { type UserCoursePageInput } from "@forento/shared/models/course"
import { toRelativeDateString } from "@forento/shared/utilities/date"
import { parseNumber } from "@forento/shared/utilities/number"

import { useCourseDetail, type CourseDetail } from "~/api/course"
import { type PaypalOrder, PurchaseSuccessModalContextProvider } from "~/contexts/PurchaseSuccessModalContext"
import { useUser } from "~/contexts/UserContext"
import Button from "~/themes/school/components/Button"
import FullLoadingPage from "~/themes/school/components/FullLoadingPage"
import Hero from "~/themes/school/components/Hero"
import { ArrowIcon } from "~/themes/school/components/Icon"
import Layout from "~/themes/school/components/Layout"
import { PageBreadcrumb } from "~/themes/school/components/PageHeader"
import PurchaseItem from "~/themes/school/components/PurchaseItem"
import { ControlledTabs } from "~/themes/school/components/Tabs"
import { useLocale, useTranslation } from "~/translations"
import routes from "~/utilities/routes"

import useTabs from "./useTabs"

const CourseDetailPage: FC = () => {
	const courseId = parseNumber(useParams().courseId!)
	const [data, reload] = useCourseDetail(courseId ?? -1)
	const t = useTranslation()

	return (
		<Layout
			metadata={
				data.status === "success"
					? {
							title: data.title,
							description: data.course.shortDescription,
							imageUrl: data.course.thumbnailFilePath ?? undefined,
						}
					: undefined
			}
		>
			<PageBreadcrumb
				path={[{ title: t("course.title"), link: routes.course.index() }]}
				title={data.status === "success" ? data.course.title : "..."}
			/>
			{(() => {
				switch (data.status) {
					case "error":
						return t("failedToLoad")
					case "loading":
						return <FullLoadingPage />
					case "not-found":
						return <Navigate to={routes.course.index()} />
					case "success": {
						return <Content data={data} inputs={data.inputs} reload={reload} />
					}
				}
			})()}
		</Layout>
	)
}

type ContentProps = {
	data: Extract<CourseDetail, { status: "success" }>
	inputs: UserCoursePageInput[]
	reload(): Promise<unknown>
}
const Content: FC<ContentProps> = ({ data, inputs, reload }) => {
	const tabs = useTabs(data.course, inputs, reload)
	const user = useUser()
	const t = useTranslation()
	const locale = useLocale()

	const [paypalOrder, setPaypalOrder] = useState<PaypalOrder | null>(null)

	const callToAction: JSX.Element = (() => {
		switch (data.button.variant) {
			case "start":
				return (
					<CallToAction>
						<Button variant="primary" onClick={routes.course.player(data.course.id)}>
							<CallToActionIcon as={ArrowIcon} />
							{t("course.start")}
						</Button>
						{data.button.expireDate !== null && (
							<p>
								{t("course.accessExpires", {
									date: toRelativeDateString(data.button.expireDate, locale),
								})}
							</p>
						)}
					</CallToAction>
				)
			case "continue":
				return (
					<CallToAction>
						<Button variant="primary" onClick={routes.course.player(data.course.id)}>
							<CallToActionIcon as={ArrowIcon} />
							{t("course.continue")}
						</Button>
						{data.button.expireDate !== null && (
							<p>
								{t("course.accessExpires", {
									date: toRelativeDateString(data.button.expireDate, locale),
								})}
							</p>
						)}
					</CallToAction>
				)
			case "purchase":
				return (
					<PurchaseItem
						item={{
							type: "course",
							id: data.course.id,
							name: data.course.title,
							shortDescription: data.course.shortDescription,
							price: data.button.price,
							accessTimeMonths: data.button.accessTimeMonths,
						}}
						onPurchase={async ({ paypalOrder }) => {
							setPaypalOrder(paypalOrder)
							await reload()
						}}
					/>
				)
			case "membership":
				return (
					<CallToAction>
						<p>{t("product.onlyForMembers")}</p>
						<Button variant="primary" onClick={routes.membership.index()}>
							{t("membership.title")}
						</Button>
					</CallToAction>
				)
			case "bokadero-membership":
				return (
					<CallToAction>
						<p>Gratis för medlemmar i Bokadero Arena</p>
						{user.user ? (
							<Button variant="primary" onClick={routes.user.settings()}>
								Hantera inställningar
							</Button>
						) : (
							<Button variant="primary" onClick={routes.user.signup()}>
								Skapa konto
							</Button>
						)}
					</CallToAction>
				)
			case "paused":
				return (
					<CallToAction>
						<p>{t("product.paused")}</p>
					</CallToAction>
				)
		}
	})()

	return (
		<PurchaseSuccessModalContextProvider content={{ type: "course", id: data.course.id }} paypalOrder={paypalOrder}>
			<Hero
				thumbnailUrl={data.course.thumbnailFilePath}
				title={data.course.title}
				description={data.course.shortDescription}
				tags={data.course.tags}
				callToAction={callToAction}
			/>
			<MoreContent>
				<ControlledTabs tabs={tabs} />
			</MoreContent>
		</PurchaseSuccessModalContextProvider>
	)
}

const CallToAction = styled.div`
	max-width: 300px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
	text-align: center;
`

const CallToActionIcon = styled.div`
	width: 16px;
	height: 16px;
`

const MoreContent = styled.div`
	background-color: white;
	box-shadow: 0px 4px 30px 0px rgba(223, 232, 255, 0.25);
	padding: 24px;

	@media (min-width: 1000px) {
		padding: 48px 86px;
	}
`

export default CourseDetailPage
